/**
 * Valueguard API Authentication Service
 * Handles OAuth2 password flow authentication with VG API
 */

let accessToken = null;
let refreshToken = null;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api-prod.valueguard.se';
const CLIENT_ID = 'MCzlK1nheYwYBykmpnsRBilN05qCc4yG';
const CLIENT_SECRET = 'wISmqeyLNF6AyuR6ToNtG6LnRuaZ0HFGIdahKK3BUpKB2TUGV6ZTztkglM6I07wQ';
const AUDIENCE = 'https://api.valueguard.se';

/**
 * Login with username and password using OAuth2 password grant
 * @param {string} username - User email/username
 * @param {string} password - User password
 * @returns {Promise<Object>} Authentication response with tokens
 */
export async function login(username, password) {
    try {
        const response = await fetch(`${API_BASE_URL}/oauth/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                grant_type: 'password',
                username,
                password,
                audience: AUDIENCE,
                client_id: CLIENT_ID,
                client_secret: CLIENT_SECRET,
            }).toString()
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error_description || 'Authentication failed');
        }

        const data = await response.json();
        
        // Store tokens
        accessToken = data.access_token;
        refreshToken = data.refresh_token;
        
        // Store tokens in localStorage for persistence
        localStorage.setItem('vg_access_token', accessToken);
        if (refreshToken) {
            localStorage.setItem('vg_refresh_token', refreshToken);
        }

        return data;
    } catch (error) {
        console.error('Login failed:', error);
        throw error;
    }
}

/**
 * Get current access token
 * @returns {Promise<string>} Valid access token
 */
export async function getAccessToken() {
    // Try to get from memory first
    if (accessToken) {
        return accessToken;
    }

    // Try to get from localStorage
    const storedToken = localStorage.getItem('vg_access_token');
    if (storedToken) {
        accessToken = storedToken;
        return accessToken;
    }

    // If we have a refresh token, try to refresh
    const storedRefreshToken = localStorage.getItem('vg_refresh_token');
    if (storedRefreshToken) {
        try {
            const refreshed = await refreshAccessToken();
            return refreshed.access_token;
        } catch (error) {
            console.error('Token refresh failed:', error);
            // Clear invalid tokens
            clearTokens();
            throw new Error('Authentication required');
        }
    }

    throw new Error('No authentication token available');
}

/**
 * Refresh access token using refresh token
 * @returns {Promise<Object>} New token data
 */
export async function refreshAccessToken() {
    const storedRefreshToken = refreshToken || localStorage.getItem('vg_refresh_token');
    
    if (!storedRefreshToken) {
        throw new Error('No refresh token available');
    }

    try {
        const response = await fetch(`${API_BASE_URL}/oauth/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                grant_type: 'refresh_token',
                refresh_token: storedRefreshToken,
                client_id: CLIENT_ID,
                client_secret: CLIENT_SECRET,
            }).toString()
        });

        if (!response.ok) {
            throw new Error('Token refresh failed');
        }

        const data = await response.json();
        
        // Update tokens
        accessToken = data.access_token;
        if (data.refresh_token) {
            refreshToken = data.refresh_token;
        }
        
        // Update localStorage
        localStorage.setItem('vg_access_token', accessToken);
        if (data.refresh_token) {
            localStorage.setItem('vg_refresh_token', data.refresh_token);
        }

        return data;
    } catch (error) {
        console.error('Token refresh failed:', error);
        clearTokens();
        throw error;
    }
}

/**
 * Check if user is currently authenticated
 * @returns {boolean} True if authenticated
 */
export function isAuthenticated() {
    return !!(accessToken || localStorage.getItem('vg_access_token'));
}

/**
 * Logout and clear all tokens
 */
export function logout() {
    clearTokens();
}

/**
 * Clear all stored tokens
 */
function clearTokens() {
    accessToken = null;
    refreshToken = null;
    localStorage.removeItem('vg_access_token');
    localStorage.removeItem('vg_refresh_token');
}

/**
 * Initialize authentication on app startup
 * Restores tokens from localStorage
 */
export function initializeAuth() {
    accessToken = localStorage.getItem('vg_access_token');
    refreshToken = localStorage.getItem('vg_refresh_token');
}